import { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Download, Share2, Check, FileImage, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

gsap.registerPlugin(ScrollTrigger);

export function ShareCardSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // ENTRANCE (0% - 30%)
      scrollTl.fromTo(
        headlineRef.current,
        { x: '-18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0
      );

      scrollTl.fromTo(
        cardRef.current,
        { x: '55vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0.05
      );

      scrollTl.fromTo(
        chipRef.current,
        { y: '18vh', opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.15
      );

      // SETTLE (30% - 70%) - hold position

      // EXIT (70% - 100%)
      scrollTl.fromTo(
        cardRef.current,
        { y: 0, opacity: 1 },
        { y: '-18vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(headlineRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.75);

      scrollTl.fromTo(chipRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.78);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleGenerateImage = async () => {
    if (!shareCardRef.current) return;

    setIsGenerating(true);

    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(shareCardRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });

      // Download the image
      const link = document.createElement('a');
      link.download = 'receipt-proof-card.png';
      link.href = dataUrl;
      link.click();

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="section-pinned z-50 flex items-center justify-center bg-[#F6F5F2]"
    >
      {/* Headline Group */}
      <div
        ref={headlineRef}
        className="absolute"
        style={{ left: '9vw', top: '10vh', width: '44vw' }}
      >
        <h2
          className="font-display font-bold leading-[1.05] tracking-[-0.02em] text-[#0B0D10]"
          style={{ fontSize: 'clamp(34px, 3.6vw, 52px)' }}
        >
          Share the receipt.
        </h2>
        <p
          className="mt-4 leading-relaxed text-[#6B6F78]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)', width: '38vw' }}
        >
          Generate a clean citation card in one tap. Optimized for threads, carousels, and video
          overlays.
        </p>

        {/* Generate Button */}
        <div className="mt-8">
          <Button
            onClick={handleGenerateImage}
            disabled={isGenerating}
            className="btn-hover flex items-center gap-2 rounded-full bg-[#E1062C] px-6 py-3 font-medium text-white hover:bg-[#c00525]"
          >
            {showSuccess ? (
              <>
                <Check className="h-4 w-4" />
                Downloaded!
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                {isGenerating ? 'Generating...' : 'Generate card'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Share Card (Vertical) */}
      <div
        ref={cardRef}
        className="absolute"
        style={{ left: '54vw', top: '18vh', width: '34vw', height: '64vh' }}
      >
        <div
          ref={shareCardRef}
          className="receipt-card flex h-full w-full flex-col overflow-hidden"
        >
          {/* Red Header */}
          <div className="flex items-center justify-between bg-[#E1062C] p-5">
            <span className="font-display text-xl font-bold tracking-[0.05em] text-white">
              PROOF
            </span>
            <Share2 className="h-5 w-5 text-white" />
          </div>

          {/* Card Body */}
          <div className="flex flex-1 flex-col p-6">
            {/* Quote */}
            <div className="flex-1">
              <p
                className="font-medium leading-relaxed text-[#0B0D10]"
                style={{ fontSize: 'clamp(16px, 1.4vw, 20px)' }}
              >
                "…the minimum wage shall be $7.25 per hour…"
              </p>
            </div>

            {/* Source Info */}
            <div className="mt-6 border-t border-[rgba(11,13,16,0.08)] pt-6">
              <p className="font-medium text-[#0B0D10]">Fair Labor Standards Act</p>
              <p className="mt-1 font-mono text-sm text-[#6B6F78]">Date: 2009-07-24</p>
            </div>

            {/* Canonical source marker */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0B0D10]">
                <Link2 className="h-6 w-6 text-white" />
              </div>
              <div className="text-right">
                <p className="font-mono text-xs text-[#6B6F78]">Canonical source</p>
                <p className="font-mono text-xs text-[#E1062C]">Link included</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[rgba(11,13,16,0.08)] bg-[#F6F5F2] px-6 py-3">
            <p className="text-center font-mono text-xs text-[#6B6F78]">Generated by Duly Cited</p>
          </div>
        </div>
      </div>

      {/* Export Format Chip */}
      <div ref={chipRef} className="absolute" style={{ left: '9vw', top: '74vh', width: '18vw' }}>
        <div className="receipt-card-sm flex items-center gap-3 p-4">
          <FileImage className="h-8 w-8 text-[#E1062C]" />
          <div>
            <p className="text-sm font-medium text-[#0B0D10]">PNG export</p>
            <p className="font-mono text-xs text-[#6B6F78]">Ready to share</p>
          </div>
        </div>
      </div>
    </section>
  );
}
